import React from 'react';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  PortalPaginationControlsRow,
  PortalPaginationEllipsis,
  PortalPaginationIconButton,
  PortalPaginationLoadingSlot,
  PortalPaginationMeta,
  PortalPaginationNavButton,
  PortalPaginationPageButton,
  PortalPaginationPageTrack,
  PortalPaginationRoot,
} from '../../styles/portalPagination';

/** @typedef {'ellipsis-left' | 'ellipsis-right'} EllipsisToken */

/**
 * Builds a sliding window of page numbers centered on the current page.
 * Always includes first and last page when they fall outside the window.
 */
export const getVisiblePages = (currentPage, totalPages, visibleCount) => {
  if (totalPages <= 0) {
    return [];
  }

  const windowSize = Math.max(1, visibleCount);

  if (totalPages <= windowSize + 2) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const half = Math.floor(windowSize / 2);
  let rangeStart = Math.max(2, currentPage - half);
  let rangeEnd = Math.min(totalPages - 1, rangeStart + windowSize - 1);
  rangeStart = Math.max(2, rangeEnd - windowSize + 1);

  /** @type {(number | EllipsisToken)[]} */
  const pages = [1];

  if (rangeStart > 2) {
    pages.push('ellipsis-left');
  }

  for (let page = rangeStart; page <= rangeEnd; page += 1) {
    pages.push(page);
  }

  if (rangeEnd < totalPages - 1) {
    pages.push('ellipsis-right');
  }

  pages.push(totalPages);

  return pages;
};

const PortalPaginationControls = ({
  currentPage,
  totalPages,
  onPageChange,
  visibleCount = 4,
  isLoading = false,
  sx,
}) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'));
  const effectiveVisibleCount = isCompact ? Math.min(3, visibleCount) : visibleCount;

  if (!totalPages || totalPages <= 1) {
    return null;
  }

  const pages = getVisiblePages(currentPage, totalPages, effectiveVisibleCount);
  const canGoToPrevious = currentPage > 1;
  const canGoToNext = currentPage < totalPages;

  return (
    <PortalPaginationRoot sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <PortalPaginationLoadingSlot aria-hidden={!isLoading}>
          {isLoading ? <CircularProgress size={16} color="primary" thickness={5} /> : null}
        </PortalPaginationLoadingSlot>
        <PortalPaginationMeta>
          Page {currentPage} of {totalPages}
        </PortalPaginationMeta>
      </Box>

      <PortalPaginationControlsRow>
        {isTabletDown ? (
          <PortalPaginationIconButton
            aria-label="Go to previous page"
            disabled={!canGoToPrevious}
            onClick={() => onPageChange(currentPage - 1)}
            size="small"
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </PortalPaginationIconButton>
        ) : (
          <PortalPaginationNavButton
            aria-label="Go to previous page"
            disabled={!canGoToPrevious}
            onClick={() => onPageChange(currentPage - 1)}
            startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
          >
            Previous
          </PortalPaginationNavButton>
        )}

        <PortalPaginationPageTrack>
          {pages.map((pageItem, index) =>
            typeof pageItem === 'number' ? (
              <PortalPaginationPageButton
                key={pageItem}
                active={currentPage === pageItem}
                aria-current={currentPage === pageItem ? 'page' : undefined}
                onClick={() => onPageChange(pageItem)}
              >
                {pageItem}
              </PortalPaginationPageButton>
            ) : (
              <PortalPaginationEllipsis key={`${pageItem}-${index}`} aria-hidden="true">
                …
              </PortalPaginationEllipsis>
            ),
          )}
        </PortalPaginationPageTrack>

        {isTabletDown ? (
          <PortalPaginationIconButton
            aria-label="Go to next page"
            disabled={!canGoToNext}
            onClick={() => onPageChange(currentPage + 1)}
            size="small"
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </PortalPaginationIconButton>
        ) : (
          <PortalPaginationNavButton
            aria-label="Go to next page"
            disabled={!canGoToNext}
            onClick={() => onPageChange(currentPage + 1)}
            endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
          >
            Next
          </PortalPaginationNavButton>
        )}
      </PortalPaginationControlsRow>
    </PortalPaginationRoot>
  );
};

export default PortalPaginationControls;
